import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {TimeZone} from '../../domain';
import {Disposers, HttpPromiseState, toastrHelper} from '../../lib/helpers';
import {timeZoneLoaders} from '../../domain/loaders/timeZoneLoaders';
import {AddTimeZoneStore} from './parts/AddTimeZoneStore';
import {DeleteTimeZoneStore} from './parts/DeleteTimeZoneStore';
import {EditTimeZoneStore} from './parts/EditTimeZoneStore';

export class TimeZoneComponentStore {
  @observable
  mode: 'view' | 'add' | 'edit' | 'delete';

  @observable.ref
  timeZonesState: HttpPromiseState<readonly TimeZone[]> | null;
  @observable.ref
  addTimeZone: AddTimeZoneStore | null;
  @observable.ref
  editTimeZone: EditTimeZoneStore | null;
  @observable.ref
  deleteTimeZone: DeleteTimeZoneStore | null;

  private readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.mode = 'view';
    this.timeZonesState = null;
    this.addTimeZone = this.editTimeZone = this.deleteTimeZone = null;
    this.setup();
  }

  @computed
  get isPending() {
    return (this.addTimeZone?.save?.isPending === true ||
      this.editTimeZone?.save?.isPending === true ||
      this.deleteTimeZone?.save?.isPending === true);
  }

  @action
  toViewMode = () => {
    this.mode = 'view';
    this.addTimeZone = this.editTimeZone = this.deleteTimeZone = null;
  };

  @action
  loadTimeZones = () => {
    if (this.timeZonesState?.isPending === true)
      return;
    this.timeZonesState = timeZoneLoaders.list();
  };


  @computed
  get timeZones(): readonly TimeZone[] {
    if (this.timeZonesState?.isResolved !== true)
      return [];
    return this.timeZonesState.value;
  }

  @action
  cancel = () => {
    if (this.isPending)
      return;
    this.toViewMode();
  };

  /****************************************************************************/
  @action
  beginAdd = () => {
    this.mode = 'add';
    this.addTimeZone = new AddTimeZoneStore();
  };

  @action
  completeAdd = () => {
    this.addTimeZone?.complete();
  };

  /****************************************************************************/

  @action
  beginEdit = (timeZone: TimeZone) => {
    this.mode = 'edit';
    this.editTimeZone = new EditTimeZoneStore(timeZone);
  };

  @action
  completeEdit = () => {
    this.editTimeZone?.complete();
  };

  /****************************************************************************/

  @action
  beginDelete = (timeZone: TimeZone) => {
    this.mode = 'delete';
    this.deleteTimeZone = new DeleteTimeZoneStore(timeZone);
    this.completeDelete();
  };

  @action
  completeDelete = () => {
    this.deleteTimeZone?.complete();
  };

  /****************************************************************************/

  setup() {
    this.disposers.add(reaction(
      () => [
        this.addTimeZone?.save?.state,
        this.editTimeZone?.save?.state,
        this.deleteTimeZone?.save?.state],
      this.onSaveStateChange));
  }

  @action
  private onSaveStateChange = () => {
    for (let state of [
      this.addTimeZone?.save,
      this.editTimeZone?.save,
      this.deleteTimeZone?.save])
      if (state?.isResolved === true) {
        toastrHelper.success(state.value);
        this.toViewMode();
        this.loadTimeZones();
      }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}
